import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, of, race, timer } from 'rxjs';
import { map } from 'rxjs/operators';

/** The browser's geolocation, or `null` where there is none. A token so specs can stand in for the browser. */
export const GEOLOCATION = new InjectionToken<Geolocation | null>('GEOLOCATION', {
  providedIn: 'root',
  factory: () => (typeof navigator !== 'undefined' && navigator.geolocation) || null,
});

/**
 * Where the customer is, best effort (KOR-15237): evidence recorded with their consents when the browser gives
 * it. Never errors and never waits longer than the timeout - a denied prompt, an iframe without the permission
 * or a slow fix all just give nothing, and the customer carries on.
 */
@Injectable({ providedIn: 'root' })
export class GeolocationService {
  private geolocation = inject(GEOLOCATION);

  current = (timeoutMs = 3000): Observable<GeolocationCoordinates | undefined> => {
    const geolocation = this.geolocation;
    if (!geolocation) {
      return of(undefined);
    }
    const position$ = new Observable<GeolocationCoordinates | undefined>((subscriber) => {
      geolocation.getCurrentPosition(
        (position) => {
          subscriber.next(position.coords);
          subscriber.complete();
        },
        () => {
          subscriber.next(undefined);
          subscriber.complete();
        },
        { timeout: timeoutMs, maximumAge: 60_000 },
      );
    });
    return race(position$, timer(timeoutMs).pipe(map(() => undefined)));
  };
}
